import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getProfileData,
  loadProfileData,
  saveProfileData,
  ProfileData
} from "../services/userService.tsx";
import { getAllOffices } from "../../data_management/services/officeService.tsx";
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Paper,
  Grid,
  Divider,
} from "@mui/material";
import { Edit, Save, Close } from "@mui/icons-material";

interface ProfileState extends ProfileData {
  // Extend if needed, or use ProfileData directly
}

const UserProfile = ({ user }: { user: any }) => {
  const [profile, setProfile] = useState<ProfileState | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Load profile data
  useEffect(() => {
    const loadData = async () => {
      try {
        // 1. Check route state first (data passed from navigation)
        if (location.state) {
          setProfile(location.state as ProfileState);
          saveProfileData(location.state as ProfileData);
          return;
        }

        // 2. Check localStorage
        const localData = loadProfileData();
        if (localData) {
          setProfile(localData as ProfileState);
          return;
        }

        // 3. Fetch fresh data using email from user prop or localStorage
        const emailToFetch = user?.email || localData?.email;
        if (emailToFetch) {
          const freshData = await getProfileData(emailToFetch);
          setProfile(freshData as ProfileState);
          saveProfileData(freshData);
        } else {
          // Fallback to empty state if no email is available
          setProfile({
            id: '',
            name: "N/A",
            email: "N/A",
            roles: "N/A",
            role: "N/A",
            status: "N/A",
            division: "N/A",
            office: "N/A",
            password: "********"
          });
        }
      } catch (error) {
        console.error("Failed to load profile:", error);
        // Fallback to empty state on error
        setProfile({
          id: '',
          name: "N/A",
          email: "N/A",
          roles: "N/A",
          role: "N/A",
          status: "N/A",
          division: "N/A",
          office: "N/A",
          password: "********"
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [location.state, user?.email]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile(prev => prev ? { ...prev, [name]: value } : null);
  };

  const handleSave = () => {
    if (!profile) return;
    setIsEditing(false);
    saveProfileData(profile);
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (!profile) return <Typography>No profile data available</Typography>;

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          User Profile
        </Typography>
        {/* {isEditing ? (
          <Box>
            <Button
              startIcon={<Close />}
              onClick={() => setIsEditing(false)}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              startIcon={<Save />}
              variant="contained"
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        ) : (
          <Button
            startIcon={<Edit />}
            variant="contained"
            onClick={() => setIsEditing(true)}
            disabled={loading}
          >
            Edit Profile
          </Button>
        )} */}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Avatar sx={{ width: 120, height: 120, mb: 2, fontSize: "3rem" }}>
                {profile.name.charAt(0)}
              </Avatar>
              {isEditing && (
                <Button variant="outlined" size="small" sx={{ mb: 3 }}>
                  Change Photo
                </Button>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nama Pegawai"
                  name="name"
                  value={profile.name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  variant={isEditing ? "outlined" : "filled"}
                />
              </Grid>

              {/* Add roles field if needed */}
              {profile.roles && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Roles"
                    name="roles"
                    value={profile.roles}
                    disabled
                    variant="filled"
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={profile.email}
                  onChange={handleInputChange}
                  disabled
                  variant={isEditing ? "outlined" : "filled"}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Status"
                  name="status"
                  value={profile.status}
                  disabled
                  variant="filled"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Division"
                  name="division"
                  value={profile.division}
                  disabled
                  variant="filled"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Office"
                  name="office"
                  value={profile.office}
                  disabled
                  variant="filled"
                />
              </Grid>

              
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="secondary"
            sx={{ px: 4 }}
            onClick={() =>
              navigate("/change-password", {
                state: {
                  currentPassword: profile.password.replace(/./g, "•"),
                },
              })
            }
          >
            Ubah Password
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserProfile;